import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateCompanyImages } from '../../../../store/AuthReducer';
import type { RootState } from '../../../../store/store';
import { JWTAuthorization } from '../../../../utils/JWTHeader';
import FileSelector from '../../../common/FileSelector/FileSelector';
import { showToast } from '../../../common/Notification';
import Spinner from '../../../common/Spinner';
import SquareImageUploader from '../../../common/SquareImageUploader/SquareImageUploader';

export interface LogoStepHandle {
  isValid: () => boolean;
  getPartialDraft: () => Record<string, never>;
  focusFirstInvalid: () => void;
}

const LogoStep = forwardRef<LogoStepHandle>((_, ref) => {
  const dispatch = useDispatch();
  const company = useSelector((s: RootState) => s.auth.company);
  const jwt = useSelector((s: RootState) => s.auth.token);
  const [isSavingMain, setIsSavingMain] = useState(false);

  const handleMainImageUpload = async(e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !company?.id) { return; }

    const formData = new FormData();
    formData.append('file', file);
    setIsSavingMain(true);
    try {
      const response = await fetch(`/api/companies/${company.id}/branding-image`, {
        method: 'POST',
        headers: { Authorization: JWTAuthorization(jwt) },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error((errorData && errorData.errors && errorData.errors.file) || 'Échec du téléchargement');
      }
      const data = await response.json();
      dispatch(updateCompanyImages({ brandingImageName: data?.brandingSettings?.brandingImageName }));
      showToast('success', 'Logo principal enregistré.');
    } catch (err) {
      console.error(err);
      showToast('error', 'Échec du téléchargement du logo');
    } finally {
      setIsSavingMain(false);
    }
  };

  useImperativeHandle(ref, () => ({
    isValid: () => true,
    getPartialDraft: () => ({}),
    focusFirstInvalid: () => {},
  }), []);

  return (
    <div>
      <h3 className="mb-4 text-lg font-semibold text-gray-800">Logos de l'entreprise</h3>
      <p className="mb-6 text-sm text-gray-500">
        Deux logos peuvent être configurés : un logo principal au format paysage utilisé dans vos newsletters
        et communications, et un logo carré dédié à votre blog public.
      </p>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Main landscape logo */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="mb-1 text-base font-semibold text-gray-800">Logo principal</h4>
          <p className="mb-4 text-xs text-gray-500">
            Utilisé dans vos newsletters et la majorité de vos communications. Un format paysage
            (plus large que haut) est recommandé pour un rendu optimal.
          </p>
          <div className="space-y-4">
            {company?.brandingSettings?.brandingImageName && (
              <div className="flex flex-col items-center gap-2">
                <img
                  src={company.brandingSettings.brandingImageName}
                  alt="Logo principal actuel"
                  className="h-24 w-48 rounded border bg-gray-50 object-contain p-2"
                />
                <p className="text-sm text-gray-500">Logo actuel — paysage</p>
              </div>
            )}
            <FileSelector
              accept="image/jpeg, image/png"
              onChange={handleMainImageUpload}
              disabled={isSavingMain}
            />
            {isSavingMain && (
              <div className="flex items-center text-sm text-gray-500">
                <Spinner size="sm" className="mr-2" />Sauvegarde…
              </div>
            )}
          </div>
        </div>

        {/* Square blog logo */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="mb-1 text-base font-semibold text-gray-800">Logo carré pour le blog</h4>
          <p className="mb-4 text-xs text-gray-500">
            Utilisé sur votre blog public. L'image sera recadrée au format carré si nécessaire.
          </p>
          {company?.id ? (
            <SquareImageUploader
              currentImage={company.brandingSettings?.brandingBlogImageName}
              uploadUrl={`/api/companies/${company.id}/branding-blog-image`}
              jwt={jwt}
              extractPath={(data) => (data as { brandingSettings?: { brandingBlogImageName?: string } } | null)?.brandingSettings?.brandingBlogImageName}
              onUploaded={(path) => dispatch(updateCompanyImages({ brandingBlogImageName: path }))}
              previewAlt="Logo blog actuel"
              previewCaption="Logo actuel — carré"
              previewSizeClass="h-28 w-28"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
});

LogoStep.displayName = 'LogoStep';

export default LogoStep;
